import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  getFileIdentityKey,
  IAttachmentUploadUrlResponse,
  IUploadedAttachment,
  validateSelectedFiles,
} from 'utils';
import {
  AttachmentPipelineStatus,
  IAttachment,
  IAttachmentPipelineHandle,
} from 'views/components/UI/AttachmentUpload/attachment.interface';
import { startAttachmentPipeline } from 'views/components/UI/AttachmentUpload/attachmentPipeline';

let attachmentIdCounter = 0;

function createAttachmentId(): string {
  attachmentIdCounter += 1;
  return `attachment-${Date.now()}-${attachmentIdCounter}`;
}

interface IUseAttachmentUpload {
  attachments: IAttachment[];
  hasBlockingAttachments: boolean;
  addFiles: (files: File[]) => void;
  removeAttachment: (id: string) => void;
}

function useAttachmentUpload(
  requestUploadUrl: (
    fileName: string,
    contentType: string,
  ) => Promise<IAttachmentUploadUrlResponse>,
  // Best-effort quarantine cleanup — read via a ref (below) so callers don't
  // need to memoize it themselves and so it never stales the unmount cleanup.
  deletePendingUpload: (storageKey: string) => Promise<void>,
): IUseAttachmentUpload {
  const [attachments, setAttachments] = useState<IAttachment[]>([]);
  const pipelineHandlesRef = useRef(new Map<string, IAttachmentPipelineHandle>());
  const attachmentsRef = useRef<IAttachment[]>([]);
  const deletePendingUploadRef = useRef(deletePendingUpload);

  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);

  useEffect(() => {
    deletePendingUploadRef.current = deletePendingUpload;
  }, [deletePendingUpload]);

  useEffect(
    () => (): void => {
      pipelineHandlesRef.current.forEach(handle => handle.cancel());
      pipelineHandlesRef.current.clear();

      // The form was cancelled/closed (Modal unmounts on close) rather than
      // saved — sweep any files that finished uploading but were never
      // linked. Harmless to also fire this after a successful save: the
      // backend just 400s ("already linked to an exercise") and we ignore it.
      attachmentsRef.current.forEach(attachment => {
        if (attachment.storageKey) {
          deletePendingUploadRef.current(attachment.storageKey).catch(() => {});
        }
      });
    },
    [],
  );

  const updateAttachmentStatus = useCallback(
    (
      id: string,
      status: AttachmentPipelineStatus,
      uploaded?: IUploadedAttachment,
    ): void => {
      setAttachments(current =>
        current.map(attachment =>
          attachment.id === id
            ? {
                ...attachment,
                status,
                name: uploaded?.fileName ?? attachment.name,
                storageKey: uploaded?.storageKey,
              }
            : attachment,
        ),
      );
    },
    [],
  );

  const addFiles = useCallback(
    (files: File[]): void => {
      const existingKeys = attachments
        .filter(attachment => attachment.status !== 'rejected')
        .map(attachment => getFileIdentityKey(attachment.file));

      const { accepted, rejected } = validateSelectedFiles(files, existingKeys);

      const acceptedAttachments: IAttachment[] = accepted.map(file => {
        const id = createAttachmentId();
        const handle = startAttachmentPipeline(
          file,
          requestUploadUrl,
          (status, uploaded) => updateAttachmentStatus(id, status, uploaded),
        );
        pipelineHandlesRef.current.set(id, handle);

        return { id, file, name: file.name, size: file.size, status: 'uploading' };
      });

      const rejectedAttachments: IAttachment[] = rejected.map(({ file, reason }) => ({
        id: createAttachmentId(),
        file,
        name: file.name,
        size: file.size,
        status: 'rejected',
        rejectionReason: reason,
      }));

      setAttachments(current => [
        ...current,
        ...acceptedAttachments,
        ...rejectedAttachments,
      ]);
    },
    [attachments, updateAttachmentStatus, requestUploadUrl],
  );

  const removeAttachment = useCallback((id: string): void => {
    pipelineHandlesRef.current.get(id)?.cancel();
    pipelineHandlesRef.current.delete(id);
    setAttachments(current => {
      const target = current.find(attachment => attachment.id === id);
      if (target?.storageKey) {
        deletePendingUploadRef.current(target.storageKey).catch(() => {});
      }
      return current.filter(attachment => attachment.id !== id);
    });
  }, []);

  const hasBlockingAttachments = useMemo(
    () =>
      attachments.some(
        attachment =>
          attachment.status === 'uploading' ||
          attachment.status === 'error' ||
          attachment.status === 'rejected',
      ),
    [attachments],
  );

  return { attachments, hasBlockingAttachments, addFiles, removeAttachment };
}

export { useAttachmentUpload };
